import type { AnimationEvent } from 'react';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATIC_IMAGE_URLS } from 'modules/core/constants';
import { useSelector } from 'react-redux';
import { IFetchingVehicleFlags, ILenderVehicle, IRACVehicle, IUser } from 'modules/core/types';
import { getUser } from 'store/selectors/auth.selector';
import { thousandFormatter } from 'modules/core/utils';
import { getIsAutoInventoryImportLoading } from 'store/selectors/ui.selector';

import {
  SuggestedVehiclesContent,
  ViewVehiclesButton,
  SuggestedVehiclesCannotMakeItWorkLabel,
  SuggestedVehiclesLoaderContainer,
  InventoryLoadingState,
  InventoryLoadingLabel,
  InventoryRevealWrapper,
} from './styles';
import { Loader } from '../../Loader';

interface ISuggestedVehiclesBlock {
  sortedVehiclesSelector: any;
  vehiclesFetchingFlagsSelector: any;
  viewOtherVehiclesClick: Function;
  startRecalculateAnimations: Function;
  stopRecalculateAnimations: Function;
  isPendingRecalculation: boolean;
}

export const SuggestedVehiclesBlock: FC<ISuggestedVehiclesBlock> = ({
  sortedVehiclesSelector,
  vehiclesFetchingFlagsSelector,
  viewOtherVehiclesClick,
  startRecalculateAnimations,
  stopRecalculateAnimations,
  isPendingRecalculation,
}) => {
  const { t } = useTranslation();
  const sortedVehicles = useSelector(sortedVehiclesSelector) as ILenderVehicle[] | IRACVehicle[];
  const { isFetchingVehicles } = useSelector(vehiclesFetchingFlagsSelector) as IFetchingVehicleFlags;
  const user = useSelector(getUser) as IUser;
  const isSilentFourEyesImportUser = !user?.withWholesaleInventory && !!user?.foureyesWebsiteId;
  const isAutoInventoryImportLoading = useSelector(getIsAutoInventoryImportLoading);
  const showImportInventoryLoader = isSilentFourEyesImportUser && isAutoInventoryImportLoading;

  const [isRevealingInventory, setIsRevealingInventory] = useState(false);
  const prevImportLoadingRef = useRef(showImportInventoryLoader);
  const pendingInventoryRevealAfterImportRef = useRef(false);

  useEffect(() => {
    const wasImporting = prevImportLoadingRef.current;

    prevImportLoadingRef.current = showImportInventoryLoader;

    if (wasImporting && !showImportInventoryLoader) {
      pendingInventoryRevealAfterImportRef.current = true;
    }
  }, [showImportInventoryLoader]);

  useEffect(() => {
    if (pendingInventoryRevealAfterImportRef.current && sortedVehicles != null && !showImportInventoryLoader) {
      pendingInventoryRevealAfterImportRef.current = false;
      setIsRevealingInventory(true);
    }
  }, [sortedVehicles, showImportInventoryLoader]);

  const handleRevealAnimationEnd = useCallback((event: AnimationEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;

    setIsRevealingInventory(false);
  }, []);

  useEffect(() => {
    if (isFetchingVehicles) {
      startRecalculateAnimations(false, false);
    } else if (!isFetchingVehicles && !isPendingRecalculation) {
      stopRecalculateAnimations();
    }
    // `sortedVehicles` re-asserts the animation state once the vehicle list settles.
  }, [
    sortedVehicles,
    isFetchingVehicles,
    startRecalculateAnimations,
    isPendingRecalculation,
    stopRecalculateAnimations,
  ]);

  if (showImportInventoryLoader) {
    return (
      <SuggestedVehiclesContent>
        <InventoryLoadingState>
          <Loader isWithMargins={false} />
          <InventoryLoadingLabel>{t('components.loanVariablesModal.labels.loadingInventory')}</InventoryLoadingLabel>
        </InventoryLoadingState>
      </SuggestedVehiclesContent>
    );
  }

  return (
    <SuggestedVehiclesContent>
      <SuggestedVehiclesCannotMakeItWorkLabel>
        {t('components.loanVariablesModal.labels.cannotMakeItWork')}
      </SuggestedVehiclesCannotMakeItWorkLabel>
      <div>{t('components.loanVariablesModal.labels.tryDifferentVehicle')}</div>
      {isFetchingVehicles && (
        <SuggestedVehiclesLoaderContainer>
          <Loader isWithMargins={false} />
        </SuggestedVehiclesLoaderContainer>
      )}
      {!isFetchingVehicles && sortedVehicles && (
        <InventoryRevealWrapper $animate={isRevealingInventory} onAnimationEnd={handleRevealAnimationEnd}>
          <ViewVehiclesButton onClick={() => viewOtherVehiclesClick()}>
            {t('components.loanVariablesModal.labels.viewVehicles', {
              count: sortedVehicles.length,
              countFormatted: thousandFormatter(sortedVehicles.length),
            })}
            <img src={STATIC_IMAGE_URLS.icons.arrowRightWhite} alt="" />
          </ViewVehiclesButton>
        </InventoryRevealWrapper>
      )}
    </SuggestedVehiclesContent>
  );
};
